import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import ScreenRotationAltIcon from "@mui/icons-material/ScreenRotationAlt";
import CloseIcon from "@mui/icons-material/Close";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import ImageIcon from "@mui/icons-material/Image";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import VreVideoEditDialog from "../../components/VreVideoEditDialog";
import MRSSThumbnailCreator from "../../components/MRSSThumbnailCreator";
import RawVideoPicker from "../../components/RawVideoPicker";
import { VideoSource, ThumbnailSource } from "../../Enums/enums";
import RemoveVideoButton from "./RemoveVideoButton";

/**
 * What an untouched (video, feed) pair defaults to.
 *
 * Local Upload rather than Original: pushes here are overwhelmingly a local
 * file, so defaulting to ORIGINAL meant changing the radio on every single
 * pair. The tradeoff is that an untouched pair now reads "Not configured"
 * until a file is picked (isVideoSourcePairResolved below returns false for
 * LOCAL with no file), so the wizard can no longer be clicked through on
 * defaults alone — which is the intended behaviour, not an oversight.
 *
 * Exported and shared so index.js's three "default config" sites can't
 * drift from this one.
 */
export const DEFAULT_PAIR_CONFIG = {
  video_source: VideoSource.LOCAL,
  thumbnail_source: ThumbnailSource.ORIGINAL,
};

/**
 * Whether a single (video, feed) pair's config is fully resolved and safe
 * to submit — i.e. every source it claims to use actually has its file/
 * payload attached. Exported so index.js's isStepValid can block
 * progression/submit on the same rule the "Ready"/"Not configured" chip
 * below already uses, instead of duplicating it.
 */
export const isVideoSourcePairResolved = (cfg) => {
  // An untouched pair is evaluated against DEFAULT_PAIR_CONFIG rather than
  // waved through: now that the default is Local Upload, "untouched" means
  // "still needs a file", not "needs nothing".
  const c = cfg || DEFAULT_PAIR_CONFIG;
  if (c.video_source === VideoSource.LOCAL && !c.local_video_file) return false;
  if (c.video_source === VideoSource.EDITED && !c.render_payload) return false;
  if (c.thumbnail_source === ThumbnailSource.CUSTOM && !c.local_thumb_file) return false;
  return true;
};

/**
 * Step — choose how each selected video's actual file is sourced, PER FEED
 * it's being pushed into: reuse the original URL, upload a local file, or
 * edit it in the Video Editor. Configured independently per (video, feed) pair —
 * the same video can go out ORIGINAL to one feed and EDITED (its own
 * watermark/template) to another — mirrors PlatformSourceCard.js's per-
 * platform independence (Brand Publish keys source config per platform, not
 * once per video; here "feed" is the equivalent axis). Any watermark/
 * branding comes from whichever Video Editor template is picked inside the
 * editor — same mechanism as Brand Publish, no separate control exists there
 * either. A (video, feed) pair left untouched defaults to Local Upload
 * (DEFAULT_PAIR_CONFIG), so it needs a file picked before it can be
 * submitted.
 *
 * Thumbnail (Original/Custom) is its OWN independent control, same as
 * PlatformSourceCard.js — available regardless of which video source is
 * picked, not nested under Local Upload.
 *
 * Picking a Local Upload video or Custom thumbnail here does NOT upload
 * anything yet — it only stores the raw File object client-side
 * (local_video_file/local_thumb_file). The actual chunk upload to
 * CreatorVault's server happens later, in index.js's handleSubmit, right
 * before that (video, feed) pair's push is sent — this step is purely
 * configuration, no network activity, matching "nothing costly happens
 * before you actually click Submit."
 */
export default function StepVideoSource({
  selectedVideos,
  groupFeeds,
  selectedFeedIds,
  videoSourceConfig,
  onVideoSourceChange,
  onRemoveVideo,
}) {
  const [editDialogOpenFor, setEditDialogOpenFor] = useState(null); // `${unique_key}::${feed_id}` or null
  const [thumbnailCreatorOpenFor, setThumbnailCreatorOpenFor] = useState(null);
  const [rawVideosByPair, setRawVideosByPair] = useState({}); // pairKey -> selected raw videos | null

  const feeds = groupFeeds.filter((f) => selectedFeedIds.includes(f.id));
  const pairKey = (uniqueKey, feedId) => `${uniqueKey}::${feedId}`;

  const cfgFor = (uniqueKey, feedId) =>
    videoSourceConfig[uniqueKey]?.[feedId] || DEFAULT_PAIR_CONFIG;

  const isResolved = (uniqueKey, feedId) => isVideoSourcePairResolved(cfgFor(uniqueKey, feedId));

  // Stores a blob preview URL alongside the File — same pairing as
  // handleThumbFile below — so "Capture from Video" (MRSSThumbnailCreator)
  // can scrub THIS freshly-picked local file instead of only ever being able
  // to fall back to the WGID's original S3 video.
  const handleLocalFile = (video, feedId, file, prevPreview) => {
    if (prevPreview) URL.revokeObjectURL(prevPreview);
    onVideoSourceChange(video.unique_key, feedId, {
      video_source: VideoSource.LOCAL,
      local_video_file: file,
      local_video_preview: URL.createObjectURL(file),
    });
  };

  // Stores a blob URL preview alongside the File — mirrors
  // PlatformSourceCard.js's handleThumbnailFile, which pairs
  // thumbnailFile/thumbnailPreview the same way — so the chosen image can be
  // rendered as a real <img> instead of just filename text below.
  const handleThumbFile = (video, feedId, file) => {
    const previewUrl = URL.createObjectURL(file);
    onVideoSourceChange(video.unique_key, feedId, {
      thumbnail_source: ThumbnailSource.CUSTOM,
      local_thumb_file: file,
      thumbnail_preview: previewUrl,
    });
  };

  // "Capture from Video" hands back a base64 frame — convert to a File and
  // store it the same way a picked file would be, matching
  // PlatformSourceCard.js's handleThumbnailFromCreator conversion. The data
  // URL itself doubles as the preview (same as PlatformSourceCard.js), so no
  // separate createObjectURL call is needed here.
  const handleThumbFromCreator = (video, feedId, base64) => {
    const dataUrl = base64.startsWith("data:image") ? base64 : `data:image/jpeg;base64,${base64}`;
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    const file = new File([u8arr], `thumbnail_${video.unique_key}_${feedId}.jpg`, { type: mime });
    setThumbnailCreatorOpenFor(null);
    onVideoSourceChange(video.unique_key, feedId, {
      thumbnail_source: ThumbnailSource.CUSTOM,
      local_thumb_file: file,
      thumbnail_preview: dataUrl,
    });
  };

  const handleEditSave = (video, feedId, renderPayload) => {
    onVideoSourceChange(video.unique_key, feedId, { video_source: VideoSource.EDITED, render_payload: renderPayload });
    setEditDialogOpenFor(null);
  };

  if (feeds.length === 0) {
    return <Alert severity="warning">No feeds selected yet — go back and pick at least one.</Alert>;
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Step — Configure Video Source
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Choose the actual video file sent to Admin Portal for each video, per feed — the same video
        can be Original for one feed and Edited in the Video Editor (its own watermark/template) for
        another. Thumbnail is a separate choice, available for any video source. Files you pick here
        aren't uploaded yet — that happens when you submit on the last step. Leaving a (video, feed)
        pair untouched defaults to Original video / Original thumbnail.
      </Typography>

      {selectedVideos.length === 0 && (
        <Alert severity="warning">No videos selected yet — go back and pick some first.</Alert>
      )}

      {selectedVideos.map((video) => {
        const vKey = video.unique_key;
        const originalVideoUrl =
          video.s3_url || video.original_video_url || video.portal_url || video.social_url || "";
        // Same fallback chain index.js's handleSubmit uses for
        // original_thumbnail_url — keeps "what you see here" and "what
        // Original actually sends" in sync.
        const originalThumbnailUrl =
          video.original_thumbnail_url || video.portal_thumb || video.thumbnail_url || video.thumbnail || "";
        return (
          <Paper key={vKey} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
            <Box mb={1} display="flex" alignItems="flex-start" gap={1}>
              <Box flex={1} minWidth={0}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {video.title || video.video_title || vKey}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {vKey}
                </Typography>
              </Box>
              {onRemoveVideo && (
                <RemoveVideoButton
                  video={video}
                  pairConfigs={Object.values(videoSourceConfig?.[vKey] || {})}
                  onRemove={onRemoveVideo}
                />
              )}
            </Box>

            {feeds.map((feed, idx) => {
              const key = pairKey(vKey, feed.id);
              const cfg = cfgFor(vKey, feed.id);
              const resolved = isResolved(vKey, feed.id);
              const thumbnailSource = cfg.thumbnail_source || ThumbnailSource.ORIGINAL;
              // "Capture from Video" must scrub whatever video is actually
              // going to be pushed — the freshly-picked LOCAL file if that's
              // the chosen source, not always the WGID's original S3 video
              // (that mismatch was the bug: choosing Local Upload still
              // captured frames from the unrelated original video).
              const videoUrlForThumbnail =
                cfg.video_source === VideoSource.LOCAL && cfg.local_video_preview
                  ? cfg.local_video_preview
                  : originalVideoUrl;

              return (
                <Box key={feed.id} sx={{ pl: 1, borderLeft: "3px solid", borderColor: "divider", mt: idx > 0 ? 1.5 : 0 }}>
                  {idx > 0 && <Divider sx={{ mb: 1.5 }} />}
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <RssFeedIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={600}>
                        {feed.title || feed.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({feed._group_label})
                      </Typography>
                    </Box>
                    {resolved ? (
                      <Chip icon={<CheckCircleIcon />} label="Ready" color="success" size="small" />
                    ) : (
                      <Chip label="Not configured" size="small" color="warning" />
                    )}
                  </Box>

                  <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5}>
                    Video
                  </Typography>
                  <FormControl>
                    <RadioGroup
                      row
                      value={cfg.video_source || VideoSource.ORIGINAL}
                      onChange={(e) => onVideoSourceChange(vKey, feed.id, { video_source: e.target.value })}
                    >
                      <FormControlLabel value={VideoSource.ORIGINAL} control={<Radio size="small" />} label="Original" />
                      <FormControlLabel value={VideoSource.LOCAL} control={<Radio size="small" />} label="Local Upload" />
                      <FormControlLabel value={VideoSource.EDITED} control={<Radio size="small" />} label="Edit in Video Editor" />
                    </RadioGroup>
                  </FormControl>

                  {cfg.video_source === VideoSource.LOCAL && (
                    <Box mt={1}>
                      <Button variant="outlined" component="label" size="small">
                        {cfg.local_video_file ? "Change Video File" : "Choose Video File"}
                        <input
                          type="file"
                          hidden
                          accept="video/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            e.target.value = "";
                            if (file) handleLocalFile(video, feed.id, file, cfg.local_video_preview);
                          }}
                        />
                      </Button>
                      {cfg.local_video_file && (
                        <>
                          <Typography variant="caption" sx={{ ml: 1 }} color="success.main">
                            ✓ {cfg.local_video_file.name} ({(cfg.local_video_file.size / (1024 * 1024)).toFixed(1)} MB) — will
                            upload on submit
                          </Typography>
                          <Tooltip title="Remove selected file">
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (cfg.local_video_preview) URL.revokeObjectURL(cfg.local_video_preview);
                                onVideoSourceChange(vKey, feed.id, { local_video_file: null, local_video_preview: null });
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  )}

                  {cfg.video_source === VideoSource.EDITED && (
                    <Box mt={1}>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<MovieFilterIcon />}
                          onClick={() => setEditDialogOpenFor(key)}
                        >
                          {cfg.render_payload ? "Re-edit" : "Open Editor"}
                        </Button>
                        {cfg.render_payload && (
                          <Typography variant="caption" color="text.secondary">
                            Edited in Video Editor — configured
                          </Typography>
                        )}
                      </Box>
                      <RawVideoPicker
                        wgid={vKey}
                        onSelectionChange={(selection) =>
                          setRawVideosByPair((prev) => ({ ...prev, [key]: selection }))
                        }
                      />
                    </Box>
                  )}

                  {(cfg.video_source === VideoSource.ORIGINAL || cfg.video_source === VideoSource.LOCAL) && (
                    <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <ScreenRotationAltIcon fontSize="small" color="action" />
                        <Typography variant="body2">Convert to Vertical (9:16)</Typography>
                      </Box>
                      <Switch
                        size="small"
                        checked={!!cfg.convert_to_vertical}
                        onChange={(e) => onVideoSourceChange(vKey, feed.id, { convert_to_vertical: e.target.checked })}
                        color="primary"
                      />
                    </Box>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  {/* Thumbnail — its own control, independent of video source above,
                      same as PlatformSourceCard.js's Thumbnail selector. */}
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <ImageIcon fontSize="small" color="action" />
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      Thumbnail
                    </Typography>
                  </Box>
                  <FormControl>
                    <RadioGroup
                      row
                      value={thumbnailSource}
                      onChange={(e) => onVideoSourceChange(vKey, feed.id, { thumbnail_source: e.target.value })}
                    >
                      <FormControlLabel value={ThumbnailSource.ORIGINAL} control={<Radio size="small" />} label="Original" />
                      <FormControlLabel value={ThumbnailSource.CUSTOM} control={<Radio size="small" />} label="Custom" />
                    </RadioGroup>
                  </FormControl>

                  {/* Original — show the video's existing thumbnail as a real preview so the
                      user can confirm what "Original" will actually send, same fallback
                      chain as index.js's original_thumbnail_url and the same missing-thumbnail
                      warning pattern as PlatformSourceCard.js's Original branch. */}
                  {thumbnailSource === ThumbnailSource.ORIGINAL && (
                    originalThumbnailUrl ? (
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Box
                          component="img"
                          src={originalThumbnailUrl}
                          alt="existing thumbnail"
                          sx={{ height: 40, borderRadius: 1, border: "1px solid", borderColor: "divider" }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Existing thumbnail — will be used as-is
                        </Typography>
                      </Box>
                    ) : (
                      <Alert severity="warning" sx={{ py: 0.5, px: 1, fontSize: "0.75rem", mt: 0.5 }}>
                        No original thumbnail found for this video — choose Custom instead.
                      </Alert>
                    )
                  )}

                  {thumbnailSource === ThumbnailSource.CUSTOM && (
                    <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                      <Button variant="outlined" component="label" size="small">
                        {cfg.local_thumb_file ? "Change Image" : "Choose Image"}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            e.target.value = "";
                            if (file) handleThumbFile(video, feed.id, file);
                          }}
                        />
                      </Button>
                      <Tooltip
                        title={
                          videoUrlForThumbnail
                            ? `Scrub ${cfg.video_source === VideoSource.LOCAL && cfg.local_video_preview ? "your uploaded local video" : "the original video"} and capture a frame as thumbnail`
                            : "No video available to capture a frame from — pick a Local Upload video above, or use Choose Image"
                        }
                      >
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PhotoCameraIcon />}
                            onClick={() => setThumbnailCreatorOpenFor(key)}
                            disabled={!videoUrlForThumbnail}
                          >
                            From Video
                          </Button>
                        </span>
                      </Tooltip>
                      {cfg.video_source === VideoSource.LOCAL && cfg.local_video_preview && (
                        <Typography variant="caption" color="text.secondary">
                          (uses your uploaded local video)
                        </Typography>
                      )}
                      {cfg.local_thumb_file ? (
                        <>
                          <Box
                            component="img"
                            src={cfg.thumbnail_preview}
                            alt="thumbnail preview"
                            sx={{ height: 40, borderRadius: 1, border: "1px solid", borderColor: "divider" }}
                          />
                          <Typography variant="caption" color="success.main">
                            ✓ {cfg.local_thumb_file.name} — will upload on submit
                          </Typography>
                          <Tooltip title="Remove custom thumbnail">
                            <IconButton
                              size="small"
                              onClick={() =>
                                // Clearing the file must also fall back the source to Original —
                                // otherwise thumbnail_source stays CUSTOM with no local_thumb_file,
                                // which the backend rejects at submit time.
                                onVideoSourceChange(vKey, feed.id, {
                                  thumbnail_source: ThumbnailSource.ORIGINAL,
                                  local_thumb_file: null,
                                  thumbnail_preview: null,
                                })
                              }
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">No image selected</Typography>
                      )}
                    </Box>
                  )}

                  <VreVideoEditDialog
                    open={editDialogOpenFor === key}
                    onClose={() => setEditDialogOpenFor(null)}
                    video={video}
                    onSave={(renderPayload) => handleEditSave(video, feed.id, renderPayload)}
                    onThumbnailSave={(base64) => handleThumbFromCreator(video, feed.id, base64)}
                    rawVideos={rawVideosByPair[key]}
                    channelName={`Admin Portal Push — ${feed.title || feed.name} — ${video.title || video.video_title || vKey}`}
                  />

                  <MRSSThumbnailCreator
                    open={thumbnailCreatorOpenFor === key}
                    setOpen={(v) => setThumbnailCreatorOpenFor(v ? key : null)}
                    url={videoUrlForThumbnail}
                    onSave={(base64) => handleThumbFromCreator(video, feed.id, base64)}
                  />
                </Box>
              );
            })}
          </Paper>
        );
      })}
    </Box>
  );
}
